/**
 * Graph Explorer - Message Renderer Module
 *
 * Rich rendering for task chat messages:
 * 1. Markdown text to safe HTML (via `marked`)
 * 2. Entity link patterns [Label](type:UUID) → entity badge chips
 * 3. Tool call/response text → collapsible <details> steps
 *
 * Adapted from @merlin/chat messageRenderer.js for the Graph Explorer context.
 *
 * @module explorer-message-renderer
 */

import { marked } from 'marked';

(function(global) {
    'use strict';

    // ================================================================
    // Marked Configuration
    // ================================================================
    // Note: headerIds and mangle were removed in marked v9+
    marked.setOptions({
        breaks: true,       // GitHub-style line breaks
        gfm: true,          // GitHub Flavored Markdown
    });

    // Custom renderer: intercept entity-protocol links at the token level.
    // This ensures [Label](type:UUID) → entity badge even in marked v9+/v14
    // which may block or transform non-http/https protocols.
    const _entityLinkRenderer = {
        link({ href, title, tokens }) {
            const ENTITY_PROTO = /^(node|resource|taxonomy_resource|ontology_type|edge|concept|person|organization|location|event):/;
            if (href && ENTITY_PROTO.test(href)) {
                const colonIdx = href.indexOf(':');
                const entityType = href.substring(0, colonIdx);
                const entityId   = href.substring(colonIdx + 1);
                const label      = (tokens && tokens[0] && tokens[0].text) ? tokens[0].text : (title || entityId);
                const cssType    = entityType === 'resource' ? 'taxonomy_resource' : entityType;
                return `<span class="entity-badge entity-type-${escapeAttr(cssType)}"`
                    + ` data-entity-id="${escapeAttr(entityId)}"`
                    + ` data-entity-type="${escapeAttr(entityType)}"`
                    + ` title="${escapeAttr(entityType)}: ${escapeAttr(entityId)}">`
                    + `<span class="entity-label">${escapeHtmlLight(label)}</span></span>`;
            }
            return false;
        },
    };
    marked.use({ renderer: _entityLinkRenderer });

    // ================================================================
    // Entity Chip Rendering
    // ================================================================

    /** Known entity types and their CSS class suffixes. */
    const ENTITY_TYPES = new Set([
        'node',
        'resource',
        'taxonomy_resource',
        'ontology_type',
        'edge',
        'concept',
        'person',
        'organization',
        'location',
        'event',
    ]);

    /**
     * Regex to match entity link anchors produced by marked.
     * [Label](type:UUID) → <a href="type:UUID">Label</a> → entity badge
     *
     * Permissive: allows extra attributes (rel, target, etc.) that marked v9+ may add.
     */
    const ENTITY_LINK_REGEX = /<a\s[^>]*href="((?:node|resource|taxonomy_resource|ontology_type|edge|concept|person|organization|location|event):([^"]+))"[^>]*>([^<]+)<\/a>/gi;

    /**
     * Raw markdown entity pattern for fallback (plain text not processed by marked).
     * Matches: [Label](type:UUID)
     */
    const RAW_ENTITY_REGEX = /\[([^\]]+)\]\((node|resource|taxonomy_resource|ontology_type|edge|concept|person|organization|location|event):([^)]+)\)/g;

    /**
     * Replace entity link anchors with styled entity badge spans.
     */
    function replaceEntityLinks(html) {
        return html.replace(ENTITY_LINK_REGEX, (_match, _href, uuid, label) => {
            const colonIdx = _href.indexOf(':');
            const entityType = _href.substring(0, colonIdx);
            const entityId = uuid;
            const cssType = entityType === 'resource' ? 'taxonomy_resource' : entityType;
            return `<span class="entity-badge entity-type-${escapeAttr(cssType)}" data-entity-id="${escapeAttr(entityId)}" data-entity-type="${escapeAttr(entityType)}" title="${escapeAttr(entityType)}: ${escapeAttr(entityId)}">`
                + `<span class="entity-label">${escapeHtmlLight(label)}</span></span>`;
        });
    }

    /**
     * Replace raw markdown entity patterns with entity badge spans.
     */
    function replaceRawEntityLinks(text) {
        return text.replace(RAW_ENTITY_REGEX, (_match, label, entityType, entityId) => {
            const cssType = entityType === 'resource' ? 'taxonomy_resource' : entityType;
            return `<span class="entity-badge entity-type-${escapeAttr(cssType)}" data-entity-id="${escapeAttr(entityId)}" data-entity-type="${escapeAttr(entityType)}" title="${escapeAttr(entityType)}: ${escapeAttr(entityId)}">`
                + `<span class="entity-label">${escapeHtmlLight(label)}</span></span>`;
        });
    }

    // ================================================================
    // Markdown Rendering Pipeline
    // ================================================================

    /**
     * Render a text string as rich HTML with markdown + entity chips.
     *
     * Pipeline:
     *   1. marked() converts markdown to HTML
     *   2. replaceEntityLinks() converts entity <a> tags to badges
     *
     * @param {string} text - Raw markdown text from agent
     * @returns {string} Rendered HTML
     */
    function renderMarkdown(text) {
        if (!text) return '';
        let html = marked.parse(text);
        // Replace entity link anchors produced by marked
        html = replaceEntityLinks(html);
        // Fallback: replace raw [Label](type:UUID) patterns that marked v9+ may
        // not have converted to anchor tags (custom protocols like resource:/node:)
        html = replaceRawEntityLinks(html);
        return html;
    }

    /**
     * Render plain text with entity chip support only (no markdown).
     * Used for user messages.
     *
     * @param {string} text - Plain text
     * @returns {string} HTML with entity chips
     */
    function renderTextWithEntities(text) {
        if (!text) return '';
        let html = escapeHtml(text);
        html = replaceRawEntityLinks(html);
        return html;
    }

    // ================================================================
    // Tool Step Parsing & Rendering
    // ================================================================

    /**
     * Parse a concatenated text blob into structured segments:
     * tool_call, tool_response, transfer, and final text.
     *
     * @param {string} text - Concatenated message text
     * @returns {Array<{kind: string, text: string, tool_name?: string, detail?: string}>}
     */
    function parseToolSteps(text) {
        if (!text) return [{ kind: 'text', text: '' }];

        const combined = [];

        const toolCallRegex = /Calling tool:\s*(\S+)\s+with args:\s*([\s\S]*?)(?=Calling tool:|Tool response:|Transferring to agent:|$)/g;
        const toolResponseRegex = /Tool response:\s*(\S+)\s+with result:\s*([\s\S]*?)(?=Calling tool:|Tool response:|Transferring to agent:|$)/g;
        const transferRegex = /Transferring to agent:\s*(\S+)/g;

        let match;

        while ((match = toolCallRegex.exec(text)) !== null) {
            combined.push({ index: match.index, end: match.index + match[0].length, kind: 'tool_call', name: match[1], detail: match[2].trim() });
        }
        while ((match = toolResponseRegex.exec(text)) !== null) {
            combined.push({ index: match.index, end: match.index + match[0].length, kind: 'tool_response', name: match[1], detail: match[2].trim() });
        }
        while ((match = transferRegex.exec(text)) !== null) {
            combined.push({ index: match.index, end: match.index + match[0].length, kind: 'transfer', name: match[1], detail: '' });
        }

        combined.sort((a, b) => a.index - b.index);

        if (combined.length === 0) {
            return [{ kind: 'text', text }];
        }

        const segments = [];
        let lastIndex = 0;

        for (const item of combined) {
            if (item.index > lastIndex) {
                const beforeText = text.substring(lastIndex, item.index).trim();
                if (beforeText) {
                    segments.push({ kind: 'text', text: beforeText });
                }
            }
            segments.push({
                kind: item.kind,
                text: item.kind === 'transfer' ? `Transferring to ${item.name}` : '',
                tool_name: item.name,
                detail: item.detail,
            });
            lastIndex = item.end;
        }

        if (lastIndex < text.length) {
            const remaining = text.substring(lastIndex).trim();
            if (remaining) {
                segments.push({ kind: 'text', text: remaining });
            }
        }

        return segments;
    }

    /**
     * Render an array of content parts into HTML.
     * Handles text (markdown + entities), tool_call and tool_response parts.
     *
     * @param {Array} parts - Content parts array
     * @param {object} options
     * @param {boolean} options.isUser - Whether this is a user message
     * @returns {string} Rendered HTML
     */
    function renderContentParts(parts, { isUser = false } = {}) {
        if (!Array.isArray(parts) || parts.length === 0) return '';

        let html = '';
        let toolStepsHtml = '';
        let inToolGroup = false;

        for (const part of parts) {
            const kind = part.type || part.kind || 'text';

            if (kind === 'tool_call' || kind === 'tool_response') {
                if (!inToolGroup) {
                    toolStepsHtml = '<div class="task-tool-steps">';
                    inToolGroup = true;
                }
                toolStepsHtml += renderToolStep(part);
            } else if (kind === 'transfer') {
                if (!inToolGroup) {
                    toolStepsHtml = '<div class="task-tool-steps">';
                    inToolGroup = true;
                }
                toolStepsHtml += renderTransferStep(part);
            } else if (kind === 'text') {
                if (inToolGroup) {
                    toolStepsHtml += '</div>';
                    html += toolStepsHtml;
                    toolStepsHtml = '';
                    inToolGroup = false;
                }
                const text = part.text || '';
                if (text) {
                    if (isUser) {
                        html += `<div class="task-message-text">${renderTextWithEntities(text)}</div>`;
                    } else {
                        html += `<div class="task-message-text task-markdown">${renderMarkdown(text)}</div>`;
                    }
                }
            }
        }

        if (inToolGroup) {
            toolStepsHtml += '</div>';
            html += toolStepsHtml;
        }

        return html;
    }

    /**
     * Render a single tool call or tool response as a collapsible <details> element.
     */
    function renderToolStep(part) {
        const kind = part.type || part.kind;
        const toolName = part.tool_name || '';
        const isCall = kind === 'tool_call';
        const icon = isCall ? 'wrench' : 'check-circle';
        const label = isCall
            ? `Tool: ${escapeHtmlLight(toolName)}`
            : `Result: ${escapeHtmlLight(toolName)}`;

        let detailContent = '';
        if (isCall) {
            const args = part.tool_args || part.detail || '';
            detailContent = typeof args === 'object' ? JSON.stringify(args, null, 2) : String(args);
        } else {
            const result = part.tool_result || part.detail || '';
            detailContent = typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result);
        }

        const MAX_DETAIL_LENGTH = 2000;
        let truncated = false;
        if (detailContent.length > MAX_DETAIL_LENGTH) {
            detailContent = detailContent.substring(0, MAX_DETAIL_LENGTH);
            truncated = true;
        }

        return `<details class="task-tool-step ${isCall ? 'tool-call' : 'tool-response'}">
            <summary><i data-lucide="${icon}"></i> <span>${label}</span></summary>
            <pre class="task-tool-detail">${escapeHtml(detailContent)}${truncated ? '\n... (truncated)' : ''}</pre>
        </details>`;
    }

    /**
     * Render a transfer step.
     */
    function renderTransferStep(part) {
        const agentName = part.tool_name || part.text || 'unknown';
        return `<div class="task-tool-step transfer">
            <i data-lucide="arrow-right-circle"></i>
            <span>Transferring to ${escapeHtmlLight(agentName)}</span>
        </div>`;
    }

    // ================================================================
    // Main Render Entry Point
    // ================================================================

    /**
     * Render a complete message (agent or user) into rich HTML.
     *
     * For agent messages:
     *   - First tries structured content parts (tool_call, tool_response, text)
     *   - Falls back to parsing tool steps from concatenated text
     *   - Renders markdown + entity badges for text segments
     *
     * For user messages:
     *   - Entity badges only (no markdown)
     *
     * @param {string|Array|object} content - Message content (string, parts array, or object)
     * @param {object} options
     * @param {boolean} options.isUser - Whether this is a user message
     * @returns {string} Rendered HTML
     */
    function renderMessage(content, { isUser = false } = {}) {
        // Extract text from complex content structures
        const textContent = extractText(content);

        // For structured content (array of parts with type/kind fields)
        if (Array.isArray(content)) {
            const hasStructuredParts = content.some(p =>
                p && (p.type === 'tool_call' || p.type === 'tool_response' ||
                      p.kind === 'tool_call' || p.kind === 'tool_response' ||
                      p.type === 'transfer' || p.kind === 'transfer')
            );
            if (hasStructuredParts) {
                return renderContentParts(content, { isUser });
            }
        }

        // For plain text, try to parse tool steps from concatenated legacy format
        if (!isUser && textContent) {
            const parts = parseToolSteps(textContent);
            const hasTools = parts.some(p => p.kind !== 'text');
            if (hasTools) {
                return renderContentParts(parts, { isUser });
            }
            // No tool steps - render as markdown with entity badges
            return `<div class="task-message-text task-markdown">${renderMarkdown(textContent)}</div>`;
        }

        // User message or simple text
        if (isUser && textContent) {
            return `<div class="task-message-text">${renderTextWithEntities(textContent)}</div>`;
        }

        return escapeHtml(textContent || '');
    }

    /**
     * Render streaming content (always agent markdown, no tool-step parsing during stream).
     *
     * @param {string} text - Accumulated streaming text
     * @returns {string} Rendered HTML
     */
    function renderStreamingMessage(text) {
        if (!text) return '';
        return `<div class="task-message-text task-markdown">${renderMarkdown(text)}</div>`;
    }

    // ================================================================
    // HTML Escaping Utilities
    // ================================================================

    function escapeHtml(str) {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    function escapeHtmlLight(str) {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    function escapeAttr(str) {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    /**
     * Extract plain text from various content formats.
     */
    function extractText(content) {
        if (typeof content === 'string') return content;
        if (Array.isArray(content)) {
            return content.map(part => {
                if (typeof part === 'string') return part;
                if (part.text) return part.text;
                if (part.type === 'text' && part.text) return part.text;
                if (part.content) return part.content;
                return '';
            }).join('');
        }
        if (content && typeof content === 'object') {
            if (content.text) return content.text;
            if (content.content) return content.content;
        }
        return '';
    }

    // ================================================================
    // Public API
    // ================================================================
    global.ExplorerMessageRenderer = {
        renderMessage,
        renderStreamingMessage,
        renderMarkdown,
        renderTextWithEntities,
        renderContentParts,
        parseToolSteps,
        escapeHtml,
        escapeHtmlLight,
        escapeAttr,
        extractText,
    };

})(window);
